import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Dropdown } from 'primereact/dropdown'
import { Tooltip } from 'primereact/tooltip'
import { DateFilterState } from 'shared/date-filter/date-filter-component'
import {
  ageGroupOptions,
  colorOptions,
  genderOptions,
  hairStyleOptions,
  hatOptions,
  jacketTypeOptions,
  trousersTypeOptions,
} from '../types/ahcr-event.types'

const datePresets = [
  { label: 'Hoje', value: 'today' },
  { label: 'Ontem', value: 'yesterday' },
  { label: 'Últimos 7 dias', value: 'last7days' },
  { label: 'Últimos 30 dias', value: 'last30days' },
  { label: 'Personalizado', value: 'custom' },
]

interface AHCREventsFiltersProps {
  dateFilter: DateFilterState
  datePreset: string
  searchButtonHighlight: boolean
  isLoading: boolean
  clientId: string | null
  filteredEventsCount: number
  cameraNameFilter: string
  genderFilter: string
  hatFilter: string
  bagFilter: string
  maskFilter: string
  glassFilter: string
  thingsFilter: string
  ageGroupFilter: string
  hairStyleFilter: string
  jacketTypeFilter: string
  jacketColorFilter: string
  trousersTypeFilter: string
  trousersColorFilter: string
  uniqueCameraNames: { label: string; value: string }[]
  hasAHCRData: boolean
  layout: 'grid' | 'list'
  onExport: () => void
  onApplyDatePreset: (preset: string) => void
  onSearch: () => void
  onDateFilterChange: (prop: 'startDate' | 'endDate', value: any) => void
  onCameraNameFilterChange: (value: string) => void
  onGenderFilterChange: (value: string) => void
  onHatFilterChange: (value: string) => void
  onBagFilterChange: (value: string) => void
  onMaskFilterChange: (value: string) => void
  onGlassFilterChange: (value: string) => void
  onThingsFilterChange: (value: string) => void
  onAgeGroupFilterChange: (value: string) => void
  onHairStyleFilterChange: (value: string) => void
  onJacketTypeFilterChange: (value: string) => void
  onJacketColorFilterChange: (value: string) => void
  onTrousersTypeFilterChange: (value: string) => void
  onTrousersColorFilterChange: (value: string) => void
  onLayoutChange: (layout: 'grid' | 'list') => void
}

const AHCREventsFilters = ({
  dateFilter,
  datePreset,
  searchButtonHighlight,
  isLoading,
  clientId,
  filteredEventsCount,
  cameraNameFilter,
  genderFilter,
  hatFilter,
  bagFilter,
  maskFilter,
  glassFilter,
  thingsFilter,
  ageGroupFilter,
  hairStyleFilter,
  jacketTypeFilter,
  jacketColorFilter,
  trousersTypeFilter,
  trousersColorFilter,
  uniqueCameraNames,
  hasAHCRData,
  layout,
  onExport,
  onApplyDatePreset,
  onSearch,
  onDateFilterChange,
  onCameraNameFilterChange,
  onGenderFilterChange,
  onHatFilterChange,
  onBagFilterChange,
  onMaskFilterChange,
  onGlassFilterChange,
  onThingsFilterChange,
  onAgeGroupFilterChange,
  onHairStyleFilterChange,
  onJacketTypeFilterChange,
  onJacketColorFilterChange,
  onTrousersTypeFilterChange,
  onTrousersColorFilterChange,
  onLayoutChange,
}: AHCREventsFiltersProps) => {
  const ahcrFilterTooltip = !hasAHCRData ? 'Câmera sem dados de características AHCR' : undefined

  return (
    <div className='events-toolbar-left'>
      <div className='date-filter-section'>
        <div className='section-label'>
          <i className='pi pi-calendar' />
          <span>Período de Busca</span>
          <small>(requer nova busca)</small>
        </div>

        <div className='grid'>
          <div className='col-12 xl:col-8'>
            <div className='date-presets'>
              {datePresets.map((preset) => (
                <Button
                  key={preset.value}
                  label={preset.label}
                  className={`preset-button ${datePreset === preset.value ? 'active' : ''}`}
                  onClick={() => onApplyDatePreset(preset.value)}
                  outlined={datePreset !== preset.value}
                />
              ))}
            </div>
          </div>

          {datePreset === 'custom' && (
            <div className='col-12 xl:col-12'>
              <div className='custom-date-inputs'>
                <div className='date-input-group'>
                  <label>De:</label>
                  <Calendar
                    value={dateFilter.startDate}
                    onChange={(e: any) => onDateFilterChange('startDate', e.value)}
                    dateFormat='dd/mm/yy'
                    placeholder='Data inicial'
                    maxDate={dateFilter.endDate}
                    showIcon
                    showTime
                    stepMinute={15}
                    hourFormat='24'
                  />
                </div>
                <div className='date-input-group'>
                  <label>Até:</label>
                  <Calendar
                    value={dateFilter.endDate}
                    onChange={(e: any) => onDateFilterChange('endDate', e.value)}
                    dateFormat='dd/mm/yy'
                    placeholder='Data final'
                    maxDate={new Date()}
                    showIcon
                    showTime
                    stepMinute={15}
                    hourFormat='24'
                  />
                </div>
              </div>
            </div>
          )}

          <div className='col-12 md:col-6 xl:col-2'>
            <Button
              icon='pi pi-search'
              label='Buscar Eventos'
              className={`search-button w-full ${searchButtonHighlight ? 'highlight-pulse' : ''}`}
              onClick={onSearch}
              loading={isLoading}
            />
          </div>

          {clientId && (
            <div className='col-12 md:col-6 xl:col-2'>
              <div className='events-stats'>
                <span className='events-badge'>{filteredEventsCount}</span>
                <span className='events-stats-text'>Total de Eventos</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='client-filters-section'>
        <div className='section-label'>
          <i className='pi pi-filter' />
          <span>Filtros Rápidos</span>
          <small>(aplicados instantaneamente)</small>
        </div>

        <div className='grid'>
          {/* Câmera */}
          <div className='col-12 md:col-6 lg:col-3'>
            <div className='filter-group'>
              <span className='filter-icon'><i className='pi pi-video' /></span>
              <Dropdown
                value={cameraNameFilter}
                onChange={(e) => onCameraNameFilterChange(e.value)}
                options={uniqueCameraNames}
                placeholder='Câmera'
                className='filter-dropdown'
                showClear={cameraNameFilter !== ''}
              />
            </div>
          </div>

          {/* Gênero */}
          <div className='col-12 md:col-6 lg:col-3'>
            <div className='filter-group'>
              <span className='filter-icon'><i className='pi pi-user' /></span>
              <span className='ahcr-filter-wrapper' data-pr-tooltip={ahcrFilterTooltip} data-pr-position='top'>
                <Dropdown
                  value={genderFilter}
                  onChange={(e) => onGenderFilterChange(e.value)}
                  options={genderOptions}
                  placeholder='Gênero'
                  className='filter-dropdown'
                  showClear={genderFilter !== ''}
                  disabled={!hasAHCRData}
                />
              </span>
            </div>
          </div>

          {/* Faixa etária */}
          <div className='col-12 md:col-6 lg:col-3'>
            <div className='filter-group'>
              <span className='filter-icon'><i className='pi pi-id-card' /></span>
              <span className='ahcr-filter-wrapper' data-pr-tooltip={ahcrFilterTooltip} data-pr-position='top'>
                <Dropdown
                  value={ageGroupFilter}
                  onChange={(e) => onAgeGroupFilterChange(e.value)}
                  options={ageGroupOptions}
                  placeholder='Faixa etária'
                  className='filter-dropdown'
                  showClear={ageGroupFilter !== ''}
                  disabled={!hasAHCRData}
                />
              </span>
            </div>
          </div>

          {/* Cabelo */}
          <div className='col-12 md:col-6 lg:col-3'>
            <div className='filter-group'>
              <span className='filter-icon'><i className='pi pi-tag' /></span>
              <span className='ahcr-filter-wrapper' data-pr-tooltip={ahcrFilterTooltip} data-pr-position='top'>
                <Dropdown
                  value={hairStyleFilter}
                  onChange={(e) => onHairStyleFilterChange(e.value)}
                  options={hairStyleOptions}
                  placeholder='Cabelo'
                  className='filter-dropdown'
                  showClear={hairStyleFilter !== ''}
                  disabled={!hasAHCRData}
                />
              </span>
            </div>
          </div>

          {/* Chapéu */}
          <div className='col-12 md:col-6 lg:col-3'>
            <div className='filter-group'>
              <span className='filter-icon'><i className='pi pi-tag' /></span>
              <span className='ahcr-filter-wrapper' data-pr-tooltip={ahcrFilterTooltip} data-pr-position='top'>
                <Dropdown
                  value={hatFilter}
                  onChange={(e) => onHatFilterChange(e.value)}
                  options={hatOptions}
                  placeholder='Chapéu'
                  className='filter-dropdown'
                  showClear={hatFilter !== ''}
                  disabled={!hasAHCRData}
                />
              </span>
            </div>
          </div>

          {/* Máscara */}
          <div className='col-12 md:col-6 lg:col-3'>
            <div className='filter-group'>
              <span className='filter-icon'><i className='pi pi-tag' /></span>
              <span className='ahcr-filter-wrapper' data-pr-tooltip={ahcrFilterTooltip} data-pr-position='top'>
                <Dropdown
                  value={maskFilter}
                  onChange={(e) => onMaskFilterChange(e.value)}
                  options={hatOptions}
                  placeholder='Máscara'
                  className='filter-dropdown'
                  showClear={maskFilter !== ''}
                  disabled={!hasAHCRData}
                />
              </span>
            </div>
          </div>

          {/* Óculos */}
          <div className='col-12 md:col-6 lg:col-3'>
            <div className='filter-group'>
              <span className='filter-icon'><i className='pi pi-eye' /></span>
              <span className='ahcr-filter-wrapper' data-pr-tooltip={ahcrFilterTooltip} data-pr-position='top'>
                <Dropdown
                  value={glassFilter}
                  onChange={(e) => onGlassFilterChange(e.value)}
                  options={hatOptions}
                  placeholder='Óculos'
                  className='filter-dropdown'
                  showClear={glassFilter !== ''}
                  disabled={!hasAHCRData}
                />
              </span>
            </div>
          </div>

          {/* Carrega bolsa/objeto */}
          <div className='col-12 md:col-6 lg:col-3'>
            <div className='filter-group'>
              <span className='filter-icon'><i className='pi pi-shopping-bag' /></span>
              <span className='ahcr-filter-wrapper' data-pr-tooltip={ahcrFilterTooltip} data-pr-position='top'>
                <Dropdown
                  value={bagFilter}
                  onChange={(e) => onBagFilterChange(e.value)}
                  options={hatOptions}
                  placeholder='Bolsa'
                  className='filter-dropdown'
                  showClear={bagFilter !== ''}
                  disabled={!hasAHCRData}
                />
              </span>
            </div>
          </div>

          {/* Carrega objetos */}
          <div className='col-12 md:col-6 lg:col-3'>
            <div className='filter-group'>
              <span className='filter-icon'><i className='pi pi-briefcase' /></span>
              <span className='ahcr-filter-wrapper' data-pr-tooltip={ahcrFilterTooltip} data-pr-position='top'>
                <Dropdown
                  value={thingsFilter}
                  onChange={(e) => onThingsFilterChange(e.value)}
                  options={hatOptions}
                  placeholder='Carrega objetos'
                  className='filter-dropdown'
                  showClear={thingsFilter !== ''}
                  disabled={!hasAHCRData}
                />
              </span>
            </div>
          </div>

          {/* Tipo de blusa */}
          <div className='col-12 md:col-6 lg:col-3'>
            <div className='filter-group'>
              <span className='filter-icon'><i className='pi pi-tag' /></span>
              <span className='ahcr-filter-wrapper' data-pr-tooltip={ahcrFilterTooltip} data-pr-position='top'>
                <Dropdown
                  value={jacketTypeFilter}
                  onChange={(e) => onJacketTypeFilterChange(e.value)}
                  options={jacketTypeOptions}
                  placeholder='Tipo de blusa'
                  className='filter-dropdown'
                  showClear={jacketTypeFilter !== ''}
                  disabled={!hasAHCRData}
                />
              </span>
            </div>
          </div>

          {/* Cor da blusa */}
          <div className='col-12 md:col-6 lg:col-3'>
            <div className='filter-group'>
              <span className='filter-icon'><i className='pi pi-palette' /></span>
              <span className='ahcr-filter-wrapper' data-pr-tooltip={ahcrFilterTooltip} data-pr-position='top'>
                <Dropdown
                  value={jacketColorFilter}
                  onChange={(e) => onJacketColorFilterChange(e.value)}
                  options={colorOptions}
                  placeholder='Cor da blusa'
                  className='filter-dropdown'
                  showClear={jacketColorFilter !== ''}
                  disabled={!hasAHCRData}
                />
              </span>
            </div>
          </div>

          {/* Tipo de calça */}
          <div className='col-12 md:col-6 lg:col-3'>
            <div className='filter-group'>
              <span className='filter-icon'><i className='pi pi-tag' /></span>
              <span className='ahcr-filter-wrapper' data-pr-tooltip={ahcrFilterTooltip} data-pr-position='top'>
                <Dropdown
                  value={trousersTypeFilter}
                  onChange={(e) => onTrousersTypeFilterChange(e.value)}
                  options={trousersTypeOptions}
                  placeholder='Tipo de calça'
                  className='filter-dropdown'
                  showClear={trousersTypeFilter !== ''}
                  disabled={!hasAHCRData}
                />
              </span>
            </div>
          </div>

          {/* Cor da calça */}
          <div className='col-12 md:col-6 lg:col-3'>
            <div className='filter-group'>
              <span className='filter-icon'><i className='pi pi-palette' /></span>
              <span className='ahcr-filter-wrapper' data-pr-tooltip={ahcrFilterTooltip} data-pr-position='top'>
                <Dropdown
                  value={trousersColorFilter}
                  onChange={(e) => onTrousersColorFilterChange(e.value)}
                  options={colorOptions}
                  placeholder='Cor da calça'
                  className='filter-dropdown'
                  showClear={trousersColorFilter !== ''}
                  disabled={!hasAHCRData}
                />
              </span>
            </div>
          </div>

          <Tooltip target='.ahcr-filter-wrapper' />
        </div>

        <div className='layout-toggle-section'>
          <span className='layout-label'>Visualização:</span>
          <div className='layout-toggle-buttons'>
            <Button
              icon='pi pi-th-large'
              rounded
              text
              severity={layout === 'grid' ? 'info' : 'secondary'}
              onClick={() => onLayoutChange('grid')}
              tooltip='Grade'
              tooltipOptions={{ position: 'top' }}
            />
            <Button
              icon='pi pi-list'
              rounded
              text
              severity={layout === 'list' ? 'info' : 'secondary'}
              onClick={() => onLayoutChange('list')}
              tooltip='Lista'
              tooltipOptions={{ position: 'top' }}
            />
          </div>
          <Button
            icon='pi pi-file-export'
            label={`Exportar (${filteredEventsCount})`}
            className='p-button-outlined p-button-sm'
            onClick={onExport}
            disabled={filteredEventsCount === 0}
          />
        </div>
      </div>
    </div>
  )
}

export default AHCREventsFilters
